// ============================================
// UI - Button Component
// ============================================

import React from 'react';
import { cn } from '../lib/utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'highlight' | 'success' | 'warning' | 'danger' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed';

    const variantStyles = {
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      highlight: 'btn-highlight',
      success: 'btn-success',
      warning: 'btn-warning',
      danger: 'btn-danger',
      neutral: 'btn-neutral',
    };

    const sizes = {
      sm: 'h-8 px-3 text-sm',
      md: 'h-10 px-4 text-base',
      lg: 'h-12 px-6 text-lg',
    };

    const spinnerSizes = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variantStyles[variant], sizes[size], 'relative', className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              animation: 'spin 0.8s linear infinite'
            }}
          >
            <div 
              className={cn(spinnerSizes[size], 'border-2 border-current border-t-transparent rounded-full')}
            />
          </div>
        )}
        <span className={cn(isLoading && 'opacity-0')}>{children}</span>
      </button>
    );
  }
);

Button.displayName = 'Button';
